import * as React from 'react';
import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { faCopy, faPencil, faTrashCan, faUserPlus } from '@fortawesome/pro-regular-svg-icons';

import Table, { RowControlIconButton } from '../../core/components/Table.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import classnames from '../../core/utils/classnames.tsx';

import { FormClientRow, FormRow } from './FormsConfig.tsx';
import { DELETE_SELF_SERVICE_FORM, DELETE_SELF_SERVICE_FORM_CLIENT } from './queries.ts';

const AddClientIcon = withIcon(faUserPlus);
const CopyIcon = withIcon(faCopy);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);

const CLIENT_ROW_CLASSES = '[&>td]:bg-surface-medium';

// `truncate` (nowrap) would make the full URL the column's min-content width in the auto-layout table,
// so it could never shrink below max-w. line-clamp + break-all keeps it one line while letting it flex.
const FormUrl = ({ url }: { url: string }) => (
    <code title={url} className="line-clamp-1 break-all min-w-24 text-xs text-content-medium select-all">
        {url}
    </code>
);

interface FormsTableProps {
    forms: FormRow[];
    clients: FormClientRow[];
    loading: boolean;
    editingFormId: string | null;
    onAddClient: (form: FormRow) => void;
    onEditForm: (form: FormRow) => void;
    onChanged: () => Promise<void>;
}

const FormsTable = ({
    forms,
    clients,
    loading,
    editingFormId,
    onAddClient,
    onEditForm,
    onChanged,
}: FormsTableProps) => {
    const { t } = useTranslation('forms');
    const { publishToast } = useToaster();

    const [deleteForm] = useMutation(DELETE_SELF_SERVICE_FORM);
    const [deleteClient] = useMutation(DELETE_SELF_SERVICE_FORM_CLIENT);

    const [pendingFormDelete, setPendingFormDelete] = useState<FormRow | null>(null);
    const [pendingClientDelete, setPendingClientDelete] = useState<FormClientRow | null>(null);
    const [deleting, setDeleting] = useState(false);

    const clientsByForm = useMemo(() => {
        const grouped = new Map<string, FormClientRow[]>();
        for (const client of clients) {
            const group = grouped.get(client.form.id) ?? [];
            group.push(client);
            grouped.set(client.form.id, group);
        }
        return grouped;
    }, [clients]);

    const handleCopy = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            publishToast({ description: t('tenant.clients.toast.copySuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('tenant.clients.toast.copyError'), status: 'error' });
        }
    };

    const handleConfirmFormDelete = async () => {
        if (!pendingFormDelete) return;
        setDeleting(true);
        try {
            await deleteForm({ variables: { formId: pendingFormDelete.id } });
            await onChanged();
            publishToast({ description: t('tenant.forms.toast.deleteSuccess'), status: 'success' });
            setPendingFormDelete(null);
        } catch (e) {
            console.error(e);
            publishToast({ description: t('tenant.forms.toast.deleteError'), status: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const handleConfirmClientDelete = async () => {
        if (!pendingClientDelete) return;
        setDeleting(true);
        try {
            await deleteClient({ variables: { clientId: pendingClientDelete.id } });
            await onChanged();
            publishToast({ description: t('tenant.clients.toast.deleteSuccess'), status: 'success' });
            setPendingClientDelete(null);
        } catch (e) {
            console.error(e);
            publishToast({ description: t('tenant.clients.toast.deleteError'), status: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    if (forms.length === 0) return null;

    return (
        <>
            <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                <Table.Head>
                    <Table.Row>
                        <Table.HeadCell>{t('tenant.forms.table.name')}</Table.HeadCell>
                        <Table.HeadCell>{t('tenant.forms.table.link')}</Table.HeadCell>
                        <Table.HeadCell className="min-w-32 w-32">{t('tenant.forms.table.createdAt')}</Table.HeadCell>
                        <Table.HeadCell sticky="right" className="min-w-36 w-36" />
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {forms.map((form) => (
                        <React.Fragment key={form.id}>
                            <Table.Row>
                                <Table.Cell>
                                    <span className="font-medium text-content-strong truncate">
                                        {form.name || t('tenant.forms.unnamed')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    {form.requestsEnabled ? (
                                        <FormUrl url={form.requestUrl} />
                                    ) : (
                                        <span className="text-sm text-content-weak">
                                            {t('tenant.forms.requestsDisabled')}
                                        </span>
                                    )}
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap text-content-medium">
                                    {format(new Date(form.createdAt), 'PP')}
                                </Table.Cell>
                                <Table.Cell sticky="right" className="w-36">
                                    <div className="flex gap-1 justify-end">
                                        <RowControlIconButton
                                            onClick={() => onAddClient(form)}
                                            title={t('tenant.forms.list.addClient')}
                                        >
                                            <AddClientIcon />
                                        </RowControlIconButton>
                                        <RowControlIconButton
                                            onClick={() => onEditForm(form)}
                                            disabled={form.id === editingFormId}
                                            title={t('tenant.forms.list.edit')}
                                        >
                                            <EditIcon />
                                        </RowControlIconButton>
                                        {form.requestsEnabled && (
                                            <RowControlIconButton
                                                onClick={() => handleCopy(form.requestUrl)}
                                                title={t('tenant.forms.list.copy')}
                                            >
                                                <CopyIcon />
                                            </RowControlIconButton>
                                        )}
                                        <RowControlIconButton
                                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                            onClick={() => setPendingFormDelete(form)}
                                            title={t('tenant.forms.list.delete')}
                                        >
                                            <DeleteIcon />
                                        </RowControlIconButton>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                            {(clientsByForm.get(form.id) ?? []).map((client) => (
                                <Table.Row key={client.id} className={CLIENT_ROW_CLASSES}>
                                    <Table.Cell>
                                        <div className="flex flex-col min-w-0 pl-6">
                                            <span className="text-content-strong truncate">
                                                {client.clientName || client.clientCustomerNumber}
                                            </span>
                                            <span className="text-xs text-content-medium truncate">
                                                {[
                                                    client.clientName && client.clientCustomerNumber,
                                                    client.requesterEmail,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <FormUrl url={client.publicUrl} />
                                    </Table.Cell>
                                    <Table.Cell className="whitespace-nowrap text-content-medium">
                                        {format(new Date(client.createdAt), 'PP')}
                                    </Table.Cell>
                                    <Table.Cell sticky="right" className="w-36">
                                        <div className="flex gap-1 justify-end">
                                            <RowControlIconButton
                                                onClick={() => handleCopy(client.publicUrl)}
                                                title={t('tenant.clients.list.copy')}
                                            >
                                                <CopyIcon />
                                            </RowControlIconButton>
                                            <RowControlIconButton
                                                className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                                onClick={() => setPendingClientDelete(client)}
                                                title={t('tenant.clients.list.delete')}
                                            >
                                                <DeleteIcon />
                                            </RowControlIconButton>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </React.Fragment>
                    ))}
                </Table.Body>
            </Table>

            {pendingFormDelete && (
                <ConfirmModal
                    visible
                    title={t('tenant.forms.deleteModal.title')}
                    onConfirm={handleConfirmFormDelete}
                    onCancel={() => !deleting && setPendingFormDelete(null)}
                    labelConfirm={t('tenant.forms.deleteModal.confirm')}
                    confirmDisabled={deleting}
                    danger
                >
                    <p className="text-sm text-content-medium">
                        {t('tenant.forms.deleteModal.body', {
                            name: pendingFormDelete.name || t('tenant.forms.unnamed'),
                        })}
                    </p>
                </ConfirmModal>
            )}

            {pendingClientDelete && (
                <ConfirmModal
                    visible
                    title={t('tenant.clients.deleteModal.title')}
                    onConfirm={handleConfirmClientDelete}
                    onCancel={() => !deleting && setPendingClientDelete(null)}
                    labelConfirm={t('tenant.clients.deleteModal.confirm')}
                    confirmDisabled={deleting}
                    danger
                >
                    <p className="text-sm text-content-medium">
                        {t('tenant.clients.deleteModal.body', {
                            name: pendingClientDelete.clientName || pendingClientDelete.clientCustomerNumber,
                        })}
                    </p>
                </ConfirmModal>
            )}
        </>
    );
};

export default FormsTable;
